/**
 * Service para comunicação com a API de Metas
 */

import axios from 'axios';
import { 
  MetaRequest, 
  MetaResponse, 
  MetaResumida, 
  UsuarioMetaDto, 
  MetaFilters, 
  UsuarioFilters,
  RankingPerformanceDto,
  Result,
  Pagina
} from '../types/meta';

class MetaService {
  private baseUrl = '/api/meta';

  /**
   * Busca metas com filtros - agora retorna metas únicas com múltiplos usuários
   */
  async buscarMetas(filters: MetaFilters = {}): Promise<Result<MetaResumida[]>> {
    const params = new URLSearchParams();
    
    if (filters.termo) params.append('termo', filters.termo);
    if (filters.somenteAtivas !== undefined) params.append('somenteAtivas', filters.somenteAtivas.toString());
    if (filters.usuarioId) params.append('usuarioId', filters.usuarioId.toString());
    if (filters.status) params.append('status', filters.status);

    const response = await axios.get(`${this.baseUrl}?${params.toString()}`);
    return response.data;
  }

  async contarMetasPorStatus(): Promise<Record<string, number>> {
    try {
      const response = await axios.get(`${this.baseUrl}/contagem-por-status`);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar contagem de metas por status:", error);
      return {
        "": 0,     // Total (todas)
        "active": 0, // Em progresso
        "previous": 0, // Anteriores
        "future": 0   // Futuras
      };
    }
  }
  
  /**
   * Busca meta por ID - agora retorna uma meta com múltiplos usuários
   */
  async buscarMetaPorId(id: string): Promise<Result<MetaResponse>> {
    const response = await axios.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Cria nova meta - agora aceita múltiplos usuários
   */
  async criarMeta(meta: MetaRequest): Promise<Result<MetaResponse>> {
    const response = await axios.post(this.baseUrl, meta);
    return response.data;
  }

  /**
   * Atualiza meta existente - agora aceita múltiplos usuários
   */
  async atualizarMeta(id: string, meta: MetaRequest): Promise<Result<MetaResponse>> {
    const response = await axios.put(`${this.baseUrl}/${id}`, meta);
    return response.data;
  }

  /**
   * Exclui meta (soft delete)
   */
  async excluirMeta(id: string): Promise<Result<void>> {
    const response = await axios.delete(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Busca usuários vendedores para seleção na meta
   */
  async buscarUsuariosVendedores(): Promise<UsuarioMetaDto[]> {
    const response = await axios.get(`${this.baseUrl}/usuarios`);
    return response.data;
  }

  /**
   * Busca usuários para seleção na meta (método antigo - manter por compatibilidade)
   */
  async buscarUsuarios(filters: UsuarioFilters = {}): Promise<Pagina<UsuarioMetaDto>> {
    const params = new URLSearchParams();
    
    if (filters.filter) params.append('filter', filters.filter);
    if (filters.pageIndex !== undefined) params.append('pageIndex', filters.pageIndex.toString());
    if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());

    const response = await axios.get(`${this.baseUrl}/usuarios?${params.toString()}`);
    return response.data;
  }

  /**
   * Busca ranking de performance das metas
   */
  async buscarRankingPerformance(startDate?: string, endDate?: string): Promise<Result<RankingPerformanceDto[]>> {
    try {
      const params = new URLSearchParams();
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const url = params.toString() 
        ? `${this.baseUrl}/ranking-performance?${params.toString()}`
        : `${this.baseUrl}/ranking-performance`;
      
      const response = await axios.get(url);
      
      // Verificar se a resposta tem a estrutura esperada
      if (response.data && typeof response.data === 'object') {
        return response.data;
      } else {
        return {
          isSuccess: false,
          message: 'Resposta da API em formato inesperado',
          data: undefined
        };
      }
    } catch (error: any) {
      // Retornar erro estruturado
      return {
        isSuccess: false,
        message: error.response?.data?.message || error.message || 'Erro ao buscar ranking de performance',
        data: undefined
      };
    }
  }

  /**
   * Busca ranking de performance para uma meta específica
   */
  async buscarRankingPerformancePorMeta(metaId: number): Promise<Result<RankingPerformanceDto[]>> {
    try {
      const url = `${this.baseUrl}/${metaId}/ranking-performance`;
      
      const response = await axios.get(url);
      
      // Verificar se a resposta tem a estrutura esperada
      if (response.data && typeof response.data === 'object') {
        return response.data;
      } else {
        return {
          isSuccess: false,
          message: 'Resposta da API em formato inesperado',
          data: undefined
        };
      }
    } catch (error: any) {
      // Retornar erro estruturado
      return {
        isSuccess: false,
        message: error.response?.data?.message || error.message || 'Erro ao buscar ranking de performance por meta',
        data: undefined
      };
    }
  }
}

export const metaService = new MetaService();
